export type VisualStyle =
  | 'none'
  | 'festival'
  | 'psychedelic'
  | 'plasma'
  | 'vortex'
  | 'strobe-burst'
  | 'flash'
  | 'neon-journey'
  | 'horizon-journey'
  | 'cinematic'
  | 'minimal'
  | 'emotional'
  | 'aggressive'
  | 'cyberpunk'
  | 'space';

export type VisualStyleGroup = 'live' | 'imported' | 'cinema' | 'worlds';
export type LyricDisplayMode = 'hooks' | 'line' | 'full' | 'off';

export type SongSection =
  | 'intro'
  | 'verse'
  | 'build'
  | 'chorus'
  | 'drop'
  | 'bridge'
  | 'outro';

export type SceneId =
  | 'neon-tunnel'
  | 'rainbow-tunnel'
  | 'particle-galaxy'
  | 'kinetic-typography'
  | 'laser-grid'
  | 'starfield-warp'
  | 'sacred-geometry'
  | 'bass-crystal'
  | 'festival-crowd'
  | 'cyberpunk-city'
  | 'lyric-constellation'
  | 'album-art-explosion'
  | 'waveform-terrain'
  | 'cathedral-of-light'
  | 'bird-flight'
  | 'spectrum-bars-2d'
  | 'radial-eq-2d'
  | 'oscilloscope-2d'
  | 'waveform-ribbon-2d'
  | 'beat-kaleidoscope-2d'
  | 'lyric-poster-2d'
  | 'album-slices-2d'
  | 'bass-orbit-2d'
  | 'grid-pulse-2d'
  | 'color-fields-2d'
  | 'dot-matrix-2d'
  | 'lissajous-lasers-2d'
  | 'vinyl-rings-2d'
  | 'skyline-eq-2d'
  | 'plasma-visualizer-2d'
  | 'vortex-visualizer-2d'
  | 'strobe-visualizer-2d'
  | 'flash-visualizer-2d'
  | 'journey-visualizer-2d'
  | 'journey-2-visualizer-2d'
  | 'bird-journey-2d'
  | 'doodle-quest-2d';

export type TransitionType = 'cut' | 'fade' | 'flash' | 'zoom' | 'glitch';

export interface ColorPalette {
  primary: [number, number, number];
  secondary: [number, number, number];
  accent: [number, number, number];
  background: [number, number, number];
  glow: [number, number, number];
}

export interface LyricCue {
  startMs: number;
  endMs: number;
  text: string;
  normalizedText: string;
  weight: number;
  isRepeated: boolean;
  isLikelyHook: boolean;
}

export interface VisualEvent {
  startMs: number;
  endMs: number;
  sceneId: SceneId;
  section: SongSection;
  transition: TransitionType;
  intensity: number;
  params: Record<string, number | string | boolean>;
}

export interface PreparedTrack {
  spotifyTrackId: string;
  title: string;
  artist: string;
  album: string;
  durationMs: number;
  albumArtUrl: string;
  bpm: number;
  palette: ColorPalette;
  lyrics: LyricCue[];
  visualTimeline: VisualEvent[];
  hooks: LyricCue[];
  preparedAt: number;
  hasLyrics: boolean;
  audioProfile: TrackAudioProfile;
}

export type BpmSource = 'spotify-features' | 'spotify-analysis' | 'manual' | 'tap' | 'mic-estimated' | 'fallback';

export interface TrackAudioProfile {
  bpm: number;
  bpmSource: BpmSource;
  confidence: number;
  energy: number;
  danceability: number;
  valence: number;
  loudness: number;
  timeSignature: number;
  beatOffsetMs: number;
}

export interface PlaybackSnapshot {
  trackId: string | null;
  title: string;
  artist: string;
  album: string;
  albumArtUrl: string;
  durationMs: number;
  progressMs: number;
  isPlaying: boolean;
  timestamp: number;
  deviceName: string;
  deviceType: string;
}

export interface BeatState {
  bpm: number;
  beatIndex: number;
  beatPhase: number;
  isBeat: boolean;
  beatStrength: number;
  barIndex: number;
}

export interface LiveOverrides {
  intensityOffset: number;
  lyricsEnabled: boolean;
  lyricDisplayMode: LyricDisplayMode;
  forceLyricMode: boolean;
  blackout: boolean;
  strobeBoost: number;
  sceneLocked: boolean;
  forcedSceneId: SceneId | null;
}

export interface QueueTrackPreview {
  id: string;
  title: string;
  artist: string;
  albumArtUrl: string;
  durationMs?: number;
  prepared?: boolean;
}

export const SCENE_IDS: SceneId[] = [
  'neon-tunnel',
  'rainbow-tunnel',
  'particle-galaxy',
  'kinetic-typography',
  'laser-grid',
  'starfield-warp',
  'sacred-geometry',
  'bass-crystal',
  'festival-crowd',
  'cyberpunk-city',
  'lyric-constellation',
  'album-art-explosion',
  'waveform-terrain',
  'cathedral-of-light',
  'bird-flight',
  'spectrum-bars-2d',
  'radial-eq-2d',
  'oscilloscope-2d',
  'waveform-ribbon-2d',
  'beat-kaleidoscope-2d',
  'lyric-poster-2d',
  'album-slices-2d',
  'bass-orbit-2d',
  'grid-pulse-2d',
  'color-fields-2d',
  'dot-matrix-2d',
  'lissajous-lasers-2d',
  'vinyl-rings-2d',
  'skyline-eq-2d',
  'plasma-visualizer-2d',
  'vortex-visualizer-2d',
  'strobe-visualizer-2d',
  'flash-visualizer-2d',
  'journey-visualizer-2d',
  'journey-2-visualizer-2d',
  'bird-journey-2d',
  'doodle-quest-2d',
];

export const SCENE_3D_IDS: SceneId[] = [
  'neon-tunnel',
  'rainbow-tunnel',
  'particle-galaxy',
  'kinetic-typography',
  'laser-grid',
  'starfield-warp',
  'sacred-geometry',
  'bass-crystal',
  'festival-crowd',
  'cyberpunk-city',
  'lyric-constellation',
  'album-art-explosion',
  'waveform-terrain',
  'cathedral-of-light',
  'bird-flight',
];

export const SCENE_2D_IDS: SceneId[] = [
  'spectrum-bars-2d',
  'radial-eq-2d',
  'oscilloscope-2d',
  'waveform-ribbon-2d',
  'beat-kaleidoscope-2d',
  'lyric-poster-2d',
  'album-slices-2d',
  'bass-orbit-2d',
  'grid-pulse-2d',
  'color-fields-2d',
  'dot-matrix-2d',
  'lissajous-lasers-2d',
  'vinyl-rings-2d',
  'skyline-eq-2d',
  'plasma-visualizer-2d',
  'vortex-visualizer-2d',
  'strobe-visualizer-2d',
  'flash-visualizer-2d',
  'journey-visualizer-2d',
  'journey-2-visualizer-2d',
  'bird-journey-2d',
  'doodle-quest-2d',
];

export type SceneDimension = '3d' | '2d';

export const SCENE_DIMENSION: Record<SceneId, SceneDimension> = {
  'neon-tunnel': '3d',
  'rainbow-tunnel': '3d',
  'particle-galaxy': '3d',
  'kinetic-typography': '3d',
  'laser-grid': '3d',
  'starfield-warp': '3d',
  'sacred-geometry': '3d',
  'bass-crystal': '3d',
  'festival-crowd': '3d',
  'cyberpunk-city': '3d',
  'lyric-constellation': '3d',
  'album-art-explosion': '3d',
  'waveform-terrain': '3d',
  'cathedral-of-light': '3d',
  'bird-flight': '3d',
  'spectrum-bars-2d': '2d',
  'radial-eq-2d': '2d',
  'oscilloscope-2d': '2d',
  'waveform-ribbon-2d': '2d',
  'beat-kaleidoscope-2d': '2d',
  'lyric-poster-2d': '2d',
  'album-slices-2d': '2d',
  'bass-orbit-2d': '2d',
  'grid-pulse-2d': '2d',
  'color-fields-2d': '2d',
  'dot-matrix-2d': '2d',
  'lissajous-lasers-2d': '2d',
  'vinyl-rings-2d': '2d',
  'skyline-eq-2d': '2d',
  'plasma-visualizer-2d': '2d',
  'vortex-visualizer-2d': '2d',
  'strobe-visualizer-2d': '2d',
  'flash-visualizer-2d': '2d',
  'journey-visualizer-2d': '2d',
  'journey-2-visualizer-2d': '2d',
  'bird-journey-2d': '2d',
  'doodle-quest-2d': '2d',
};

export const VISUAL_STYLE_GROUPS: { id: VisualStyleGroup; label: string }[] = [
  { id: 'live', label: 'Live' },
  { id: 'imported', label: 'Imported' },
  { id: 'cinema', label: 'Cinema' },
  { id: 'worlds', label: 'Worlds' },
];

export const VISUAL_STYLES: { id: VisualStyle; label: string; group: VisualStyleGroup }[] = [
  { id: 'none', label: 'No Effects', group: 'live' },
  { id: 'festival', label: 'Festival', group: 'live' },
  { id: 'psychedelic', label: 'Psychedelic', group: 'live' },
  { id: 'aggressive', label: 'Aggressive', group: 'live' },
  { id: 'strobe-burst', label: 'Strobe Burst', group: 'live' },
  { id: 'plasma', label: 'Plasma', group: 'imported' },
  { id: 'vortex', label: 'Vortex', group: 'imported' },
  { id: 'flash', label: 'Flash', group: 'imported' },
  { id: 'neon-journey', label: 'Neon Journey', group: 'imported' },
  { id: 'cinematic', label: 'Cinematic', group: 'cinema' },
  { id: 'minimal', label: 'Minimal', group: 'cinema' },
  { id: 'emotional', label: 'Emotional', group: 'cinema' },
  { id: 'horizon-journey', label: 'Horizon Journey', group: 'worlds' },
  { id: 'cyberpunk', label: 'Cyberpunk', group: 'worlds' },
  { id: 'space', label: 'Space', group: 'worlds' },
];

export const DEFAULT_BPM = 128;
